import { TranslateService } from '@ngx-translate/core';
import {
  BLACK,
  FONT_NAME,
  FOOTER_HEIGHT,
  GRAY,
  HEADER_HEIGHT,
  HEADING_FONT_SIZE,
  JSPDF_HEADING_STYLES,
  JSPDF_TABLE_BASE_STYLES,
  JSPDF_TYPE_BOLD,
  JSPDF_TYPE_PLAIN,
  LINE_WIDTH,
  LOGO_HEIGHT,
  LOGO_WIDTH,
  MAX_PRODUCT_CLAIMS_ROWS,
  PADDING,
  PAGE_CONTENT_WIDTH,
  PAGE_HEIGHT,
  PAGE_MARGIN,
  PAGE_WIDTH,
  PARAGRAPH_FONT_SIZE,
  PILL_X,
  PILL_Y,
  TEXT_LINE_HEIGHT,
  X_PADDING,
} from '@shared/constants/product-page-print-constants';
import { ProductDetailViewModel } from '@product-detail/models/product-detail-view-model';
import {
  getProductStatusPills,
  getRemotePillAssets,
} from '@shared/helpers/document-creation.helpers';
import { ServiceHandlerService } from '@shared/services/service-handler.service';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import autoTable, { CellDef, Styles } from 'jspdf-autotable';

export const addImage = (
  doc: jsPDF,
  image: string,
  format: string,
  currentX: number,
  currentY: number,
  width: number,
  height: number,
) => {
  doc.addImage(image, format, currentX, currentY, width, height);

  currentX += width;
  currentY += height;
  return [currentX, currentY];
};

export const addBrandAndDescription = (
  doc: jsPDF,
  product: ProductDetailViewModel,
  currentX: number,
  currentY: number,
) => {
  currentY += PARAGRAPH_FONT_SIZE * TEXT_LINE_HEIGHT;
  doc.setFont(FONT_NAME);
  doc.setFontSize(PARAGRAPH_FONT_SIZE);
  doc.text(product?.summary?.brand ?? '', currentX, currentY);

  currentY += PADDING / 2;
  const textWidth = PAGE_WIDTH - currentX - PAGE_MARGIN;
  [currentX, currentY] = addMultiLineText(
    doc,
    product?.summary?.productDescTxtl ?? '',
    currentX,
    currentY,
    textWidth,
  );
  return [currentX, currentY];
};

export const addProductNumbersAndPackSize = (
  doc: jsPDF,
  product: ProductDetailViewModel,
  currentX: number,
  currentY: number,
) => {
  const customerProductNumber = getTextFromDOMById(
    `pdp-customer-product-number-${product?.productNumber}`,
  );

  return addSeparatedText(
    doc,
    [
      `#${product?.productNumber}`,
      ...(!!customerProductNumber ? [customerProductNumber] : []),
      `${product?.summary?.salesPackSize}`,
    ],
    currentX,
    currentY,
  );
};

export const addPricesAndInventories = (
  doc: jsPDF,
  productNumber: number,
  includeProductPrices: boolean,
  translateService: TranslateService,
  currentX: number,
  currentY: number,
) => {
  const casesPrice = getTextFromDOMById(
    `pdp-cases-price-display-${productNumber}`,
  );
  const casesInventory = formatInventoryText(
    getTextFromDOMById(`pdp-cases-inventory-display-${productNumber}`),
    translateService,
  );
  const eachesPrice = getTextFromDOMById(
    `pdp-eaches-price-display-${productNumber}`,
  );
  const eachesInventory = formatInventoryText(
    getTextFromDOMById(`pdp-eaches-inventory-display-${productNumber}`),
    translateService,
  );

  const pricesAndInventories = [
    [
      ...(!!casesPrice && includeProductPrices ? [casesPrice] : []),
      ...(!!eachesPrice && includeProductPrices ? [eachesPrice] : []),
    ],
    [
      ...(!!casesInventory ? [casesInventory] : []),
      ...(!!eachesInventory ? [eachesInventory] : []),
    ],
  ].filter(row => row.length > 0);

  autoTable(doc, {
    theme: JSPDF_TYPE_PLAIN,
    body: pricesAndInventories,
    startY: currentY,
    margin: {
      left: currentX,
    },
    tableWidth: 'wrap',
    styles: {
      halign: 'center',
      fontSize: PARAGRAPH_FONT_SIZE - 1,
      cellPadding: {
        top: 0,
        left: 0,
        bottom: PADDING / 2,
        right: 24,
      },
    },
    didDrawPage: data => {
      currentY = data.cursor.y + LINE_WIDTH;
    },
  });

  return [currentX, currentY];
};

export const addProductPills = async (
  doc: jsPDF,
  product: ProductDetailViewModel,
  currentX: number,
  currentY: number,
  serviceHandler: ServiceHandlerService,
) => {
  const pills = getProductStatusPills(product);
  let [currentXLocal, currentYLocal] = [currentX, currentY];
  for (let index = 0; index < pills.length; index++) {
    const pill = pills[index];
    let imageAdded = false;

    if (pill.type === 'url') {
      const images = await getRemotePillAssets(serviceHandler, [pill.path]);
      if (images?.length > 0) {
        doc.addImage(
          images[0].pngImageBase64,
          'png',
          currentXLocal,
          currentYLocal,
          images[0].width,
          images[0].height,
        );
        imageAdded = true;
      }
    } else {
      doc.addImage(
        pill.path,
        'png',
        currentXLocal,
        currentYLocal,
        PILL_X,
        PILL_Y,
      );
      imageAdded = true;
    }

    if (imageAdded) {
      currentXLocal += PILL_X + X_PADDING;
      if ((index + 1) % 6 === 0) {
        currentYLocal + PILL_Y;
      }
    }
  }
  return [currentXLocal, currentYLocal + PILL_Y];
};

export const addProductHighlights = (
  doc: jsPDF,
  product: ProductDetailViewModel,
  includeProductPrices: boolean,
  translateService: TranslateService,
  currentX: number,
  currentY: number,
) => {
  const productHighlights = createProductHighlightsRows(
    product,
    translateService,
  );

  const breakdownAndDimensions = createBreakdownAndDimensionsRows(
    product?.productNumber,
    includeProductPrices,
    translateService,
  );

  if (productHighlights?.length > 0 || breakdownAndDimensions?.length > 0) {
    [currentX, currentY] = addHeading(
      doc,
      'Product Highlights',
      currentX,
      currentY,
    );
  }
  if (productHighlights?.length > 0) {
    [currentX, currentY] = addSingleColumnTable(
      doc,
      productHighlights,
      doc.getCurrentPageInfo().pageNumber,
      currentX,
      currentY,
    );
  }
  if (breakdownAndDimensions?.length > 0) {
    [currentX, currentY] = addMultiColumnTable(
      doc,
      breakdownAndDimensions,
      doc.getCurrentPageInfo().pageNumber,
      currentX,
      currentY,
    );
  }

  return [currentX, currentY];
};

export const addProductClaims = (
  doc: jsPDF,
  product: ProductDetailViewModel,
  translateService: TranslateService,
  currentX: number,
  currentY: number,
) => {
  const productClaims = product?.detail?.claims
    ?.map(claim => claim?.name)
    ?.filter(claimName => !!claimName);

  for (let i = 0; i < productClaims?.length; i++) {
    productClaims[i] = productClaims[i]
      .split(' ')
      .map(word => word[0].toUpperCase() + word.substring(1))
      .join(' ');
  }

  const tableBody: string[][] = [];
  while (productClaims?.length > 0) {
    tableBody.push(productClaims.splice(0, MAX_PRODUCT_CLAIMS_ROWS));
  }

  if (tableBody.length > 0) {
    [currentX, currentY] = addHeading(
      doc,
      translateService.instant(
        'i18n.productDetail.additionalInfo.productClaims',
      ),
      currentX,
      currentY,
      0,
    );

    let maxY = currentY;
    let hasSpaceRemaining = true;
    for (let i = 0; i < tableBody.length; i++) {
      if (!hasSpaceRemaining) {
        break;
      }

      autoTable(doc, {
        theme: JSPDF_TYPE_PLAIN,
        body: tableBody[i].map(text => [text]),
        startY: currentY,
        margin: {
          top: HEADER_HEIGHT,
          left: currentX,
          bottom: FOOTER_HEIGHT,
        },
        tableWidth: 'wrap',
        styles: {
          ...JSPDF_TABLE_BASE_STYLES,
          cellPadding: 0,
        },
        willDrawPage: data => {
          if (
            currentX + data.table.columns[0].width >
            PAGE_WIDTH - PAGE_MARGIN
          ) {
            hasSpaceRemaining = false;
          }
        },
        willDrawCell: data => {
          if (!hasSpaceRemaining) {
            data.cell.text = [''];
          }
        },
        didDrawPage: data => {
          currentX = data.cursor.x + 32;
          maxY = Math.max(maxY, data.cursor.y);
        },
      });
    }

    currentY = maxY;
  }

  return [currentX, currentY];
};

export const addPreparationAndCooking = (
  doc: jsPDF,
  product: ProductDetailViewModel,
  translateService: TranslateService,
  currentX: number,
  currentY: number,
) => {
  const preparationAndCooking = createPreparationAndCookingRows(
    product,
    translateService,
  );
  if (preparationAndCooking?.length > 0) {
    [currentX, currentY] = addHeading(
      doc,
      'Preparation & Cooking',
      currentX,
      currentY,
    );
    [currentX, currentY] = addSingleColumnTable(
      doc,
      preparationAndCooking,
      doc.getCurrentPageInfo().pageNumber,
      currentX,
      currentY,
    );
  }

  return [currentX, currentY];
};

export const addShippingInfo = (
  doc: jsPDF,
  product: ProductDetailViewModel,
  translateService: TranslateService,
  currentX: number,
  currentY: number,
) => {
  const shippingInfo = createShippingInfoRows(
    product?.productNumber,
    translateService,
  );
  if (shippingInfo?.length > 0) {
    [currentX, currentY] = addHeading(
      doc,
      'Shipping Information',
      currentX,
      currentY,
    );
    [currentX, currentY] = addMultiColumnTable(
      doc,
      shippingInfo,
      doc.getCurrentPageInfo().pageNumber,
      currentX,
      currentY,
      false,
    );
  }
};

export const addIngredientsAllergensAndNutritionals = async (
  doc: jsPDF,
  product: ProductDetailViewModel,
  translateService: TranslateService,
  currentX: number,
  currentY: number,
) => {
  const currentPage = doc.getCurrentPageInfo().pageNumber;

  [currentX, currentY] = addHeading(doc, 'Product Details', currentX, currentY);
  const headerAndHeadingY = currentY;

  const nutritionFactsLabel = document.getElementById(
    `pdp-nutritional-info-${product?.productNumber}`,
  );
  const nutritionFactsLabelWidth = !!nutritionFactsLabel ? 200 : 0;

  const ingredientsTableBody = createIngredientsRows(product, translateService);
  const ingredientsAndAllergensWidth =
    PAGE_CONTENT_WIDTH - nutritionFactsLabelWidth - PADDING * 2;
  [currentX, currentY] = addSingleColumnTable(
    doc,
    ingredientsTableBody,
    doc.getCurrentPageInfo().pageNumber,
    currentX,
    currentY,
    false,
    ingredientsAndAllergensWidth,
  );

  const allergensTableBody = createAllergensRows(
    doc,
    translateService,
    ingredientsAndAllergensWidth,
  );
  autoTable(doc, {
    theme: JSPDF_TYPE_PLAIN,
    body: allergensTableBody,
    startY: currentY,
    margin: {
      top: HEADER_HEIGHT + PADDING,
      left: currentX,
      bottom: FOOTER_HEIGHT,
    },
    tableWidth: ingredientsAndAllergensWidth,
    styles: {
      ...JSPDF_TABLE_BASE_STYLES,
      cellPadding: 0,
    },
  });

  if (!!nutritionFactsLabel) {
    doc.setPage(currentPage);
    currentX += ingredientsAndAllergensWidth + PADDING * 2;
    currentY = headerAndHeadingY;
    await addNutritionals(
      doc,
      nutritionFactsLabel,
      nutritionFactsLabelWidth,
      product?.productNumber,
      currentX,
      currentY,
    );
  }
};

export const addProductImages = (
  doc: jsPDF,
  imageArray: string[],
  currentX: number,
  currentY: number,
) => {
  const tableBody: string[][] = [];
  for (let i = 0; i < Math.ceil(imageArray.length / 2); i++) {
    tableBody.push(['', '', '']);
  }

  let currIdx = 0;
  const imageSize = 164;
  autoTable(doc, {
    theme: JSPDF_TYPE_PLAIN,
    body: tableBody,
    startY: currentY,
    margin: {
      top: HEADER_HEIGHT,
      left: currentX,
      bottom: FOOTER_HEIGHT,
    },
    tableWidth: 'wrap',
    rowPageBreak: 'avoid',
    columnStyles: {
      0: { cellWidth: imageSize },
      1: { cellWidth: 64 },
      2: { cellWidth: imageSize },
    },
    styles: {
      cellPadding: 0,
      minCellHeight: imageSize + 49,
    },
    didParseCell: data => {
      if (data.row.index === tableBody.length - 1) {
        data.cell.styles.minCellHeight = imageSize;
      }
    },
    didDrawCell: data => {
      if (data.column.index % 2 === 0 && currIdx < imageArray.length) {
        doc.addImage(
          imageArray[currIdx],
          'JPEG',
          data.cursor.x,
          data.cursor.y,
          imageSize,
          imageSize,
        );
        currIdx++;
      }
    },
  });
};

export const addHeader = (
  doc: jsPDF,
  product: ProductDetailViewModel,
  currentX: number,
  currentY: number,
) => {
  currentX = addImage(
    doc,
    '../assets/images/usfoods-logo-print.webp',
    'JPEG',
    currentX,
    currentY,
    LOGO_WIDTH,
    LOGO_HEIGHT,
  )[0];

  currentX += 20;
  [currentX, currentY] = addSeparatedText(
    doc,
    [
      product?.summary?.brand?.replace(/ /g, '\u00a0'),
      `#${product?.productNumber}`,
    ],
    currentX,
    currentY,
  );

  currentY += PADDING / 2;
  const textWidth = PAGE_WIDTH - currentX - PAGE_MARGIN;
  addMultiLineText(
    doc,
    product?.summary?.productDescTxtl ?? '',
    currentX,
    currentY,
    textWidth,
  );
};

export const addFooter = (
  doc: jsPDF,
  pageNumber: number,
  pageCount: number,
  currentX: number,
  currentY: number,
) => {
  doc.setDrawColor(GRAY);
  doc.setLineWidth(LINE_WIDTH);
  doc.line(currentX, currentY, PAGE_WIDTH, currentY);

  currentX += PAGE_MARGIN;
  currentY += PADDING / 2;
  const disclaimer =
    'All product information contained herein is subject ' +
    'to change, including pricing, promotions, and ' +
    'nutritionals. For current information at the time ' +
    'of purchase, please contact your sales associate.';
  const disclaimerTextWidth = PAGE_CONTENT_WIDTH;
  [currentX, currentY] = addMultiLineText(
    doc,
    disclaimer,
    currentX,
    currentY,
    disclaimerTextWidth,
    PARAGRAPH_FONT_SIZE - 2,
    'normal',
    '#585858',
  );

  currentY += PADDING;
  doc.line(currentX, currentY, PAGE_WIDTH - PAGE_MARGIN, currentY);

  currentY += PARAGRAPH_FONT_SIZE + PADDING / 2;
  doc.setTextColor(BLACK);
  doc.setFontSize(PARAGRAPH_FONT_SIZE);
  doc.text('For the exclusive use by US Foods customers', currentX, currentY);

  const date = `Print Date: ${new Date().toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  })}`;
  const page = `Page ${pageNumber} of ${pageCount}`;
  const padding = PADDING * 3;
  const dateAndPageTextWidth =
    doc.getTextWidth(date) + doc.getTextWidth(page) + padding * 2;
  currentX = PAGE_WIDTH - PAGE_MARGIN - dateAndPageTextWidth;
  currentY -= PARAGRAPH_FONT_SIZE - 2;
  addSeparatedText(doc, [date, page], currentX, currentY, padding);
};

export const addMultiLineText = (
  doc: jsPDF,
  text: string,
  currentX: number,
  currentY: number,
  textWidth: number,
  fontSize: number = HEADING_FONT_SIZE,
  fontStyle: string = JSPDF_TYPE_BOLD,
  textColor: string = BLACK,
) => {
  currentY += fontSize * TEXT_LINE_HEIGHT;
  doc.setFont(FONT_NAME, fontStyle);
  doc.setFontSize(fontSize);
  doc.setTextColor(textColor);
  const multiLineText: string[] = doc.splitTextToSize(text, textWidth);
  doc.text(multiLineText, currentX, currentY);

  currentY += fontSize * TEXT_LINE_HEIGHT * (multiLineText.length - 1);
  return [currentX, currentY];
};

const addSeparatedText = (
  doc: jsPDF,
  text: string[],
  currentX: number,
  currentY: number,
  padding: number = PADDING,
  addSeparator: boolean = true,
) => {
  autoTable(doc, {
    theme: JSPDF_TYPE_PLAIN,
    body: [text],
    startY: currentY,
    margin: {
      left: currentX,
      bottom: 0,
    },
    tableWidth: 'wrap',
    styles: {
      ...JSPDF_TABLE_BASE_STYLES,
      halign: 'center',
      cellPadding: {
        top: 0,
        bottom: 0,
        left: padding,
        right: padding,
      },
      lineWidth: {
        right: addSeparator ? LINE_WIDTH : 0,
      },
      lineColor: GRAY,
    },
    didParseCell: data => {
      if (data.column.index === 0) {
        data.cell.styles.cellPadding = {
          left: 0,
          right: padding,
        };
      }
      if (data.column.index === text.length - 1) {
        data.cell.styles.lineWidth = 0;
      }
    },
    didDrawPage: data => {
      currentY = data.cursor.y;
    },
  });

  return [currentX, currentY];
};

export const formatInventoryText = (
  inventoryString: string,
  translateService: TranslateService,
) => {
  return inventoryString
    ?.replace(
      translateService.instant('i18n.productDetail.notAvailable'),
      'N/A',
    )
    .replace('\n', ' ');
};

export const getTextFromDOMById = (id: string) => {
  return document.getElementById(id)?.innerText;
};

const createProductHighlightsRows = (
  product: ProductDetailViewModel,
  translateService: TranslateService,
) => {
  const originGrownHarvested = getTextFromDOMById(
    `pdp-grown-harvested-${product?.productNumber}`,
  );
  const originProcessed = getTextFromDOMById(
    `pdp-processed-${product?.productNumber}`,
  );
  const overview = (
    (product?.detail?.additionalDescription ?? '') +
    (!!originGrownHarvested ? `\n\n${originGrownHarvested}` : '') +
    (!!originProcessed ? `\n\n${originProcessed}` : '')
  ).trim();

  return createSingleColumnTableBody(
    [
      translateService.instant(
        'i18n.productDetail.productDetailDescription.overview',
      ),
      translateService.instant(
        'i18n.productDetail.productDetailDescription.features',
      ),
      translateService.instant(
        'i18n.productDetail.productDetailDescription.benefits',
      ),
    ],
    [
      overview,
      formatAttributes(product?.detail?.productAttributes),
      formatAttributes(product?.detail?.benefits),
    ],
  );
};

export const getCostBreakDownArray = (
  productNumber: number,
  includeProductPrices: boolean,
) => {
  if (!productNumber) {
    return [];
  }
  const i18nPrefix = 'i18n.productDetail.';
  const costBreakdownArray = [
    `pdp-portions-${i18nPrefix}packSize-${productNumber}`,
    ...(includeProductPrices
      ? [`pdp-portions-Portion Price-${productNumber}`]
      : []),
  ];
  return costBreakdownArray.map(id => getTextFromDOMById(id));
};

export const getBoxDimensionArray = (productNumber: number) => {
  const i18nPrefix = 'i18n.productDetail.';
  const boxDimensionArray = [
    `pdp-box-dimensions-${i18nPrefix}length-${productNumber}`,
    `pdp-box-dimensions-${i18nPrefix}width-${productNumber}`,
    `pdp-box-dimensions-${i18nPrefix}height-${productNumber}`,
    `pdp-box-dimensions-${i18nPrefix}volume-${productNumber}`,
    `pdp-box-dimensions-${i18nPrefix}storage-${productNumber}`,
  ];
  return boxDimensionArray.map(id => getTextFromDOMById(id));
};
export const getShippingDetailsArray = (productNumber: number) => {
  const i18nPrefix = 'i18n.productDetail.shippingInfo.';
  const shippingDetailsArray = [
    `pdp-shippingDetail-${i18nPrefix}shippingData.grossWeight-${productNumber}`,
    `pdp-shippingDetail-${i18nPrefix}shippingData.netWeight-${productNumber}`,
    `pdp-shippingDetail-${i18nPrefix}shippingData.height-${productNumber}`,
    `pdp-shippingDetail-${i18nPrefix}shippingData.width-${productNumber}`,
    `pdp-shippingDetail-${i18nPrefix}shippingData.length-${productNumber}`,
    `pdp-shippingDetail-${i18nPrefix}shippingData.cube-${productNumber}`,
  ];
  return shippingDetailsArray.map(id => getTextFromDOMById(id));
};
export const getStorageInfoArray = (productNumber: number) => {
  const i18nPrefix = 'i18n.productDetail.shippingInfo.';
  const storageInfoArray = [
    `pdp-storageInfo-${i18nPrefix}storageData.usfStorage-${productNumber}`,
    `pdp-storageInfo-${i18nPrefix}storageData.minTemp-${productNumber}`,
    `pdp-storageInfo-${i18nPrefix}storageData.maxTemp-${productNumber}`,
  ];
  return storageInfoArray.map(id => getTextFromDOMById(id));
};
export const getManufacturerDetailsArray = (productNumber: number) => {
  const manufacturerDetailsArray = [
    `pdp-manufacturer-details-${productNumber}`,
    `pdp-manufacturer-product-number-${productNumber}`,
  ];
  return manufacturerDetailsArray.map(id => getTextFromDOMById(id));
};

const createBreakdownAndDimensionsRows = (
  productNumber: number,
  includeProductPrices: boolean,
  translateService: TranslateService,
) => {
  const i18nPrefix = 'i18n.productDetail.';
  const costBreakdownArray = [
    createCellDef('Cost Breakdown', JSPDF_HEADING_STYLES),
    ...createCellDefFromIds([
      `pdp-portions-${i18nPrefix}packSize-${productNumber}`,
      ...(includeProductPrices
        ? [`pdp-portions-Portion Price-${productNumber}`]
        : []),
    ]),
  ];
  const boxDimensionArray = [
    createCellDef(
      translateService.instant(
        'i18n.productDetail.productDetailDescription.boxDimensions',
      ),
      JSPDF_HEADING_STYLES,
    ),
    ...createCellDefFromIds([
      `pdp-box-dimensions-${i18nPrefix}length-${productNumber}`,
      `pdp-box-dimensions-${i18nPrefix}width-${productNumber}`,
      `pdp-box-dimensions-${i18nPrefix}height-${productNumber}`,
      `pdp-box-dimensions-${i18nPrefix}volume-${productNumber}`,
      `pdp-box-dimensions-${i18nPrefix}storage-${productNumber}`,
    ]),
  ];

  return createMultiColumnTableBody([costBreakdownArray, boxDimensionArray]);
};

const createPreparationAndCookingRows = (
  product: ProductDetailViewModel,
  translateService: TranslateService,
) => {
  return createSingleColumnTableBody(
    [
      translateService.instant(
        'i18n.productDetail.additionalInfo.preparationInstructions',
      ),
      translateService.instant(
        'i18n.productDetail.additionalInfo.servingSuggestions',
      ),
      translateService.instant(
        'i18n.productDetail.additionalInfo.handlingInstructions',
      ),
    ],
    [
      product?.detail?.preparationInstructions,
      product?.detail?.servingSuggestions,
      product?.detail?.handlingInstructions,
    ],
  );
};

const createShippingInfoRows = (
  productNumber: number,
  translateService: TranslateService,
) => {
  const i18nPrefix = 'i18n.productDetail.shippingInfo.';

  const shippingDetailsHeading = translateService.instant(
    'i18n.productDetail.shippingInfo.shippingDetails',
  );
  const shippingDetailsArray = [
    createCellDef(shippingDetailsHeading, JSPDF_HEADING_STYLES),
    ...createCellDefFromIds([
      `pdp-shippingDetail-${i18nPrefix}shippingData.grossWeight-${productNumber}`,
      `pdp-shippingDetail-${i18nPrefix}shippingData.netWeight-${productNumber}`,
      `pdp-shippingDetail-${i18nPrefix}shippingData.height-${productNumber}`,
      `pdp-shippingDetail-${i18nPrefix}shippingData.width-${productNumber}`,
      `pdp-shippingDetail-${i18nPrefix}shippingData.length-${productNumber}`,
      `pdp-shippingDetail-${i18nPrefix}shippingData.cube-${productNumber}`,
    ]),
  ];

  const storageInfoHeading = translateService.instant(
    'i18n.productDetail.shippingInfo.storageInformation',
  );
  const storageInfoArray = [
    createCellDef(storageInfoHeading, JSPDF_HEADING_STYLES),
    ...createCellDefFromIds([
      `pdp-storageInfo-${i18nPrefix}storageData.usfStorage-${productNumber}`,
      `pdp-storageInfo-${i18nPrefix}storageData.minTemp-${productNumber}`,
      `pdp-storageInfo-${i18nPrefix}storageData.maxTemp-${productNumber}`,
    ]),
  ];

  const manufacturerDetailsHeading = translateService.instant(
    'i18n.productDetail.shippingInfo.manufacturerDetails',
  );
  const manufacturerDetailsArray = [
    createCellDef(manufacturerDetailsHeading, JSPDF_HEADING_STYLES),
    ...createCellDefFromIds([
      `pdp-manufacturer-details-${productNumber}`,
      `pdp-manufacturer-product-number-${productNumber}`,
    ]),
  ];

  return createMultiColumnTableBody([
    shippingDetailsArray,
    storageInfoArray,
    manufacturerDetailsArray,
  ]);
};

const createIngredientsRows = (
  product: ProductDetailViewModel,
  translateService: TranslateService,
) => {
  return createSingleColumnTableBody(
    [translateService.instant('i18n.productDetail.additionalInfo.ingredients')],
    [product?.detail?.ingredients],
  );
};

const createAllergensRows = (
  doc: jsPDF,
  translateService: TranslateService,
  width: number,
) => {
  const allergenInfo = collapseSpace(
    translateService.instant(
      'i18n.productDetail.productDetailDisclaimer.information',
    ),
  );
  const importantInfoText = collapseSpace(
    translateService.instant(
      'i18n.productDetail.productDetailDisclaimer.important',
    ) +
      ' ' +
      translateService.instant(
        'i18n.productDetail.productDetailDisclaimer.importantText',
      ),
  );
  const allergenFootnote = collapseSpace(
    translateService.instant(
      'i18n.productDetail.productDetailDisclaimer.informationAllergen',
    ),
  );

  doc.setFont(FONT_NAME, 'normal');
  doc.setFontSize(PARAGRAPH_FONT_SIZE);
  const importantInfoMultiLineText: string[] = doc.splitTextToSize(
    importantInfoText,
    width,
  );

  const importantInfoFirstLine = importantInfoMultiLineText[0];
  const splitIndex = importantInfoFirstLine.indexOf(':') + 1;
  return [
    [
      createCellDef(
        'Allergens',
        { fontStyle: JSPDF_TYPE_BOLD, cellPadding: { top: PADDING } },
        2,
      ),
    ],
    [createCellDef('')],
    [createCellDef(allergenInfo, {}, 2)],
    [createCellDef('')],
    [
      createCellDef(importantInfoFirstLine.substring(0, splitIndex), {
        fontStyle: JSPDF_TYPE_BOLD,
      }),
      createCellDef(importantInfoFirstLine.substring(splitIndex)),
    ],
    ...importantInfoMultiLineText
      .slice(1)
      .map(text => [createCellDef(text, {}, 2)]),
    [createCellDef('')],
    [createCellDef(allergenFootnote, {}, 2)],
  ];
};

export const getNutritionalsTable = async (
  nutritionFactsLabel: HTMLElement,
  productNumber: number,
) => {
  if (!!nutritionFactsLabel) {
    const nutritionalDiv = document.createElement('div');
    nutritionalDiv.id = `pdp-nutritional-info-div-${productNumber}`;

    const nutritionalDivStyles = {
      width: '300pt',
      position: 'fixed',
      clipPath: 'inset(0 100% 0 0)',
      top: '100%',
    };
    for (const style in nutritionalDivStyles) {
      nutritionalDiv.style[style] = nutritionalDivStyles[style];
    }

    const cloneNutritionFactsLabel = nutritionFactsLabel.cloneNode(
      true,
    ) as HTMLElement;
    nutritionalDiv.appendChild(cloneNutritionFactsLabel);
    document.body.appendChild(nutritionalDiv);

    const canvas = await html2canvas(nutritionalDiv, {
      onclone: cloneDocument => {
        const cloneNutritionalDiv = cloneDocument.getElementById(
          `pdp-nutritional-info-div-${productNumber}`,
        );

        if (!!cloneNutritionalDiv) {
          const cloneNutritionalDivStyles = {
            clipPath: 'inset(0 0 0 0)',
            top: '0',
          };
          for (const style in nutritionalDivStyles) {
            cloneNutritionalDiv.style[style] = cloneNutritionalDivStyles[style];
          }
        }
      },
      ignoreElements: element => {
        return (
          element.nodeName === 'APP-ROOT' ||
          element.id === 'pdp-report-discrepancies'
        );
      },
    });

    nutritionalDiv.remove();
    const canvasURL = canvas.toDataURL();
    const heightToWidthRatio = canvas.height / canvas.width;
    return {
      includeNutritionFacts: true,
      nutritionFactsImage: canvasURL,
      heightToWidthRatio,
    };
  }
  return { includeNutritionFacts: false };
};

const addNutritionals = async (
  doc: jsPDF,
  nutritionFactsLabel: HTMLElement,
  width: number,
  productNumber: number,
  currentX: number,
  currentY: number,
) => {
  const nutritionalDiv = document.createElement('div');
  nutritionalDiv.id = `pdp-nutritional-info-div-${productNumber}`;

  const nutritionalDivStyles = {
    width: '300pt',
    position: 'fixed',
    clipPath: 'inset(0 100% 0 0)',
    top: '100%',
  };
  for (const style in nutritionalDivStyles) {
    nutritionalDiv.style[style] = nutritionalDivStyles[style];
  }

  const cloneNutritionFactsLabel = nutritionFactsLabel.cloneNode(
    true,
  ) as HTMLElement;
  nutritionalDiv.appendChild(cloneNutritionFactsLabel);
  document.body.appendChild(nutritionalDiv);

  const canvas = await html2canvas(nutritionalDiv, {
    onclone: cloneDocument => {
      const cloneNutritionalDiv = cloneDocument.getElementById(
        `pdp-nutritional-info-div-${productNumber}`,
      );

      if (!!cloneNutritionalDiv) {
        const cloneNutritionalDivStyles = {
          clipPath: 'inset(0 0 0 0)',
          top: '0',
        };
        for (const style in nutritionalDivStyles) {
          cloneNutritionalDiv.style[style] = cloneNutritionalDivStyles[style];
        }
      }
    },
    ignoreElements: element => {
      return (
        element.nodeName === 'APP-ROOT' ||
        element.id === 'pdp-report-discrepancies'
      );
    },
  });

  nutritionalDiv.remove();

  const heightToWidthRatio = canvas.height / canvas.width;
  const height = heightToWidthRatio * width;
  doc.addImage(canvas, 'PNG', currentX, currentY, width, height);
};

const addHeading = (
  doc: jsPDF,
  heading: string,
  currentX: number,
  currentY: number,
  bottomSeparatorWidth: number = LINE_WIDTH,
) => {
  autoTable(doc, {
    theme: JSPDF_TYPE_PLAIN,
    body: [[heading]],
    startY: currentY,
    margin: {
      top: HEADER_HEIGHT,
      left: currentX,
      bottom: FOOTER_HEIGHT,
    },
    tableWidth: PAGE_CONTENT_WIDTH,
    styles: {
      ...JSPDF_TABLE_BASE_STYLES,
      ...JSPDF_HEADING_STYLES,
      lineWidth: {
        bottom: bottomSeparatorWidth,
      },
    },
    didDrawPage: data => {
      currentY = data.cursor.y + bottomSeparatorWidth;
    },
  });

  return [currentX, currentY];
};

const createSingleColumnTableBody = (headings: string[], content: string[]) => {
  const tableBody: CellDef[][] = [];

  content.forEach((content, i) => {
    if (!!content) {
      tableBody.push(
        [
          createCellDef(i < headings.length ? headings[i] : '', {
            fontStyle: JSPDF_TYPE_BOLD,
          }),
        ],
        [createCellDef(content)],
      );
    }
  });

  return tableBody;
};

const addSingleColumnTable = (
  doc: jsPDF,
  tableBody: CellDef[][],
  currentPage: number,
  currentX: number,
  currentY: number,
  addSeparator: boolean = true,
  tableWidth: number = PAGE_CONTENT_WIDTH,
) => {
  let currentRow = 0;
  autoTable(doc, {
    theme: JSPDF_TYPE_PLAIN,
    body: tableBody,
    startY: currentY,
    margin: {
      top: HEADER_HEIGHT,
      left: currentX,
      bottom: FOOTER_HEIGHT,
    },
    tableWidth,
    styles: {
      ...JSPDF_TABLE_BASE_STYLES,
      cellPadding: {
        top: PADDING,
        bottom: PADDING,
        left: 0,
        right: 0,
      },
    },
    willDrawCell: data => {
      if (data.row.index !== -1) {
        currentRow = data.row.index;
      }
      if (
        currentRow % 2 == 0 &&
        data.cursor.y + data.row.height + 70 > PAGE_HEIGHT - FOOTER_HEIGHT
      ) {
        data.row.height += 100;
      }
      if (
        addSeparator &&
        data.row.index !== tableBody.length - 1 &&
        data.row.index % 2 === 1 &&
        currentPage === doc.getCurrentPageInfo().pageNumber
      ) {
        data.cell.styles.lineWidth = {
          bottom: LINE_WIDTH * 2,
        };
      }
      currentPage = doc.getCurrentPageInfo().pageNumber;
    },
    didDrawPage: data => {
      currentY = data.cursor.y + LINE_WIDTH;
    },
  });

  return [currentX, currentY];
};

const createMultiColumnTableBody = (rows: CellDef[][]) => {
  if (rows.every(row => row.length <= 1)) {
    return [];
  }

  let max = 0;
  rows.forEach(array => {
    if (array.length > max) {
      max = array.length;
    }
  });

  const tableBody: CellDef[][] = [];
  for (let i = 0; i < max; i++) {
    const row: CellDef[] = [];
    rows.forEach(array => {
      row.push(i < array.length ? array[i] : createCellDef(''));
    });

    tableBody.push(row);
  }

  return tableBody;
};

const addMultiColumnTable = (
  doc: jsPDF,
  tableBody: CellDef[][],
  currentPage: number,
  currentX: number,
  currentY: number,
  addTopSeparator: boolean = true,
) => {
  autoTable(doc, {
    theme: JSPDF_TYPE_PLAIN,
    body: tableBody,
    startY: currentY,
    margin: {
      top: HEADER_HEIGHT,
      left: currentX,
      bottom: FOOTER_HEIGHT,
    },
    tableWidth: PAGE_CONTENT_WIDTH,
    styles: {
      ...JSPDF_TABLE_BASE_STYLES,
      cellPadding: 0,
    },
    pageBreak: 'avoid',
    willDrawCell: data => {
      const pageNumber = doc.getCurrentPageInfo().pageNumber;
      if (
        addTopSeparator &&
        data.row.index === 0 &&
        pageNumber === currentPage
      ) {
        data.cell.styles.lineWidth = {
          top: LINE_WIDTH,
        };
      }
    },
    didDrawPage: data => {
      currentY = data.cursor.y + LINE_WIDTH + PADDING;
    },
  });

  return [currentX, currentY];
};

const createCellDef = (
  content: string,
  styles?: Partial<Styles>,
  colSpan: number = 1,
) => {
  return {
    content,
    styles: {
      ...styles,
    },
    colSpan,
  };
};

const createCellDefFromIds = (ids: string[]) => {
  return ids
    .map(id => getTextFromDOMById(id))
    .filter(content => !!content)
    .map(content => createCellDef(content));
};

const formatAttributes = (attributes: string) => {
  return attributes
    ?.split('~ ')
    .filter(attribute => attribute.length > 0)
    .map(attribute => `\u2022 ${attribute} \n`)
    .join('')
    .slice(0, -1);
};

const collapseSpace = (text: string) => {
  return text?.replace(/\s+/g, ' ').trim();
};
